type TrendPoint = {
  label: string;
  value: number;
};

type Props = {
  title: string;
  points: TrendPoint[];
  valueSuffix?: string;
  emptyLabel?: string;
  /** Libellé du dernier point (ex. « Dernier jour », « Dernière semaine »). */
  latestLabel?: string;
};

function buildSeries(points: TrendPoint[]) {
  const values = points.map((p) => p.value);
  const dataMax = Math.max(...values, 0);
  const scaleMax = Math.max(dataMax, 1);
  const polyline = values
    .map((v, i) => {
      const x = (i / Math.max(values.length - 1, 1)) * 100;
      const y = 100 - (v / scaleMax) * 100;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
  return { values, dataMax, polyline };
}

function pickAxisLabels(points: TrendPoint[]) {
  if (points.length <= 4) {
    return points;
  }
  const lastIndex = points.length - 1;
  const midIndex = Math.floor(lastIndex / 2);
  const indices = Array.from(new Set([0, midIndex, lastIndex]));
  return indices.map((i) => points[i]);
}

export default function Module10TrendChart({
  title,
  points,
  valueSuffix = '',
  emptyLabel = 'Pas encore de données sur la période.',
  latestLabel = 'Dernier point',
}: Props) {
  if (points.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-outline-variant/25 bg-slate-50/80 px-4 py-6 text-center text-xs text-on-surface-variant">
        {emptyLabel}
      </div>
    );
  }

  const { dataMax, polyline } = buildSeries(points);
  const last = points[points.length - 1]?.value ?? 0;
  const axisLabels = pickAxisLabels(points);
  const periodStart = points[0]?.label;
  const periodEnd = points[points.length - 1]?.label;
  const allZero = dataMax === 0;

  return (
    <div className="rounded-xl border border-outline-variant/15 bg-slate-50/50 px-4 py-3">
      <div className="mb-2 flex items-end justify-between gap-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-wide text-on-surface-variant">{title}</p>
          {periodStart && periodEnd ? (
            <p className="mt-0.5 text-[10px] text-on-surface-variant">
              Du {periodStart} au {periodEnd}
            </p>
          ) : null}
        </div>
        <div className="text-right">
          <p className="text-[10px] text-on-surface-variant">{latestLabel}</p>
          <p className="text-sm font-black text-primary">
            {last.toLocaleString('fr-FR')}
            {valueSuffix}
          </p>
        </div>
      </div>
      <svg
        viewBox="0 0 100 40"
        className="h-24 w-full"
        preserveAspectRatio="none"
        role="img"
        aria-label={`${title} : évolution de ${periodStart} à ${periodEnd}`}
      >
        <defs>
          <linearGradient id="module10TrendFill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="rgb(135 35 32 / 0.25)" />
            <stop offset="100%" stopColor="rgb(135 35 32 / 0.02)" />
          </linearGradient>
        </defs>
        <polygon points={`0,40 ${polyline} 100,40`} fill="url(#module10TrendFill)" />
        <polyline
          points={polyline}
          fill="none"
          stroke="#872320"
          strokeWidth="1.8"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div className="mt-1 flex justify-between gap-2 text-[10px] text-on-surface-variant">
        {axisLabels.map((p) => (
          <span key={p.label} className="truncate">
            {p.label}
          </span>
        ))}
      </div>
      <p className="mt-1 text-[10px] text-on-surface-variant">
        {allZero
          ? 'Aucune valeur enregistrée sur cette période.'
          : `Maximum sur la période : ${dataMax.toLocaleString('fr-FR')}${valueSuffix}`}
      </p>
    </div>
  );
}
